import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { useCurrentUser } from '../auth';
import {
  PRODUCT_EMBED_MANAGE_PERMISSION,
  PRODUCT_SDS_ADD_PERMISSION,
  PRODUCT_SDS_DELETE_PERMISSION,
  PRODUCT_SDS_REPLACE_PERMISSION,
  TRANSLATION_ROLLBACK_PERMISSION,
} from '../constants/permissions';
import { extractApiErrorMessage, extractApiFieldErrors, isValidationProblem } from '../apiErrors';
import {
  Product,
  TranslationSummary,
  VersionHistoryItem,
  VersionPreviewItem,
  VersionDiffItem,
  ProductEditForm,
  ProductEmbed,
  StringProductEditFields,
  copyToClipboard
} from '../components/product-details';

export const useProductDetails = () => {
  const { id } = useParams();
  const api = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const canRollbackTranslation = permissions.includes(TRANSLATION_ROLLBACK_PERMISSION);
  const canManageEmbed = permissions.includes(PRODUCT_EMBED_MANAGE_PERMISSION);
  const canAddSds = permissions.includes(PRODUCT_SDS_ADD_PERMISSION);
  const canReplaceSds = permissions.includes(PRODUCT_SDS_REPLACE_PERMISSION);
  const canDeleteSds = permissions.includes(PRODUCT_SDS_DELETE_PERMISSION);

  // Core state
  const [product, setProduct] = useState<Product | null>(null);
  const [translations, setTranslations] = useState<TranslationSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sdsError, setSdsError] = useState<string | null>(null);
  const [sdsWarning, setSdsWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Product editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<ProductEditForm>({
    name: '',
    sku: '',
    ean: '',
    ghs_pictograms: [],
  });
  const [editFieldErrors, setEditFieldErrors] = useState<Partial<Record<keyof ProductEditForm, string>>>({});
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sdsUploading, setSdsUploading] = useState(false);
  const [sdsDeleting, setSdsDeleting] = useState(false);
  const [sdsDownloading, setSdsDownloading] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embed, setEmbed] = useState<ProductEmbed | null>(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedActionLoading, setEmbedActionLoading] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  
  // Version history modal state
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedHeadId, setSelectedHeadId] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [versionHistory, setVersionHistory] = useState<VersionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<VersionPreviewItem | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [compareDiff, setCompareDiff] = useState<VersionDiffItem | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  const fetchProductData = useCallback(async () => {
    if (!id) return;

    const [productData, translationsData] = await Promise.all([
      api.get(`/products/${id}`),
      api.get(`/products/${id}/translations?includeText=true`),
    ]);
    setProduct(productData);
    setTranslations(translationsData);
    setEditForm({
      name: productData.name || '',
      sku: productData.sku || '',
      ean: productData.ean || '',
      ghs_pictograms: productData.ghs_pictograms || [],
    });
  }, [api, id]);

  // Fetch product and translations data
  useEffect(() => {
    if (!id) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchProductData();
      } catch (err) {
        setError(t('errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, fetchProductData, t]);

  // Edit handlers
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditFieldErrors({});
    if (product) {
      setEditForm({
        name: product.name || '',
        sku: product.sku || '',
        ean: product.ean || '',
        ghs_pictograms: product.ghs_pictograms || [],
      });
    }
  }, [product]);

  const handleSaveEdit = useCallback(async () => {
    if (!product || !id) return;
    
    try {
      const name = editForm.name.trim();
      if (!name) {
        setEditFieldErrors({ name: t('products.productNameRequired') });
        return;
      }

      const sku = editForm.sku.trim();
      const ean = editForm.ean.trim();

      const payload = {
        name,
        sku: sku ? sku : null,
        ean: ean ? ean : null,
        ghs_pictograms: editForm.ghs_pictograms,
      };

      const updatedProduct = await api.patch(`/products/${id}`, payload);
      setProduct(updatedProduct);
      setIsEditing(false);
      setEditFieldErrors({});
    } catch (err: unknown) {
      const apiFieldErrors = extractApiFieldErrors(err);
      const mapped: Partial<Record<keyof ProductEditForm, string>> = {};
      if (apiFieldErrors.name) mapped.name = apiFieldErrors.name;
      if (apiFieldErrors.sku) mapped.sku = apiFieldErrors.sku;
      if (apiFieldErrors.ean) mapped.ean = apiFieldErrors.ean;
      setEditFieldErrors(mapped);
      const hasFieldValidation = Object.keys(mapped).length > 0;
      if (isValidationProblem(err) || hasFieldValidation) {
        setError(t('errors.validationFixFields'));
      } else {
        setError(extractApiErrorMessage(err) || t('errors.fetchFailed'));
      }
    }
  }, [product, id, editForm, api, t]);

  const handleFormChange = useCallback((field: StringProductEditFields, value: string) => {
    setEditForm((prev: ProductEditForm) => ({ ...prev, [field]: value }));
    setEditFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handlePictogramsChange = useCallback((codes: string[]) => {
    setEditForm((prev: ProductEditForm) => ({ ...prev, ghs_pictograms: codes }));
  }, []);

  // Navigation handlers
  const handleNavigateToTranslate = useCallback(() => {
    if (!product?.id) return;
    navigate(`/products/${product.id}/translations/new`);
  }, [navigate, product?.id]);

  const handleEditTranslation = useCallback((productId: string, language: string) => {
    navigate(`/products/${productId}/translations/${language}/edit`);
  }, [navigate]);

  // Version history handlers
  const openVersionHistory = useCallback(async (headId: string, language: string) => {
    setSelectedHeadId(headId);
    setSelectedLanguage(language);
    setVersionHistoryOpen(true);
    setHistoryLoading(true);
    setPreviewVersion(null);
    setCompareDiff(null);
    
    try {
      const history = await api.get(`/translations/${headId}/versions`);
      setVersionHistory(history);
    } catch (err) {
      setError('Failed to load version history');
    } finally {
      setHistoryLoading(false);
    }
  }, [api]);

  const openVersionPreview = useCallback(async (version: number) => {
    if (!selectedHeadId) return;

    setPreviewLoading(true);
    setCompareDiff(null);
    try {
      const preview = await api.get(`/translations/${selectedHeadId}/versions/${version}`);
      setPreviewVersion(preview);
    } catch (err) {
      setError('Failed to load version preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [api, selectedHeadId]);

  const openVersionCompareWithCurrent = useCallback(async (fromVersion: number) => {
    if (!selectedHeadId || versionHistory.length === 0) return;

    const currentVersion = versionHistory[0]?.version;
    if (!currentVersion || fromVersion === currentVersion) return;

    setCompareLoading(true);
    setPreviewVersion(null);
    setCompareDiff(null);
    try {
      const diff = await api.get(`/translations/${selectedHeadId}/diff?from=${fromVersion}&to=${currentVersion}`);
      setCompareDiff(diff);
    } catch (err) {
      setError('Failed to load version comparison');
    } finally {
      setCompareLoading(false);
    }
  }, [api, selectedHeadId, versionHistory]);

  const backToVersionHistoryList = useCallback(() => {
    setPreviewVersion(null);
    setPreviewLoading(false);
    setCompareDiff(null);
    setCompareLoading(false);
  }, []);

  const rollbackTranslationVersion = useCallback(async (params: { version?: number; version_id?: string }) => {
    if (!selectedHeadId || rollbackLoading) return;

    setRollbackLoading(true);
    try {
      await api.post(`/translations/${selectedHeadId}/rollback`, params);

      const requests: Promise<any>[] = [api.get(`/translations/${selectedHeadId}/versions`)];
      if (id) requests.push(api.get(`/products/${id}/translations?includeText=true`));

      const [history, refreshedTranslations] = await Promise.all(requests);
      setVersionHistory(history);
      if (refreshedTranslations) setTranslations(refreshedTranslations);

      setPreviewVersion(null);
      setCompareDiff(null);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err) || 'Failed to restore version');
    } finally {
      setRollbackLoading(false);
    }
  }, [api, selectedHeadId, id, rollbackLoading]);

  const closeVersionHistory = useCallback(() => {
    setVersionHistoryOpen(false);
    setSelectedHeadId('');
    setSelectedLanguage('');
    setVersionHistory([]);
    setPreviewVersion(null);
    setPreviewLoading(false);
    setCompareDiff(null);
    setCompareLoading(false);
    setRollbackLoading(false);
  }, []);

  // Text copy handler
  const handleCopyText = useCallback((text: string) => {
    copyToClipboard(text);
  }, []);

  const loadEmbed = useCallback(async () => {
    if (!id || !canManageEmbed) return;
    setEmbedLoading(true);
    setEmbedError(null);
    try {
      const data = await api.get(`/products/${id}/embed`);
      setEmbed(data);
    } catch (err: unknown) {
      setEmbedError(extractApiErrorMessage(err) || t('productDetails.embedLoadFailed'));
    } finally {
      setEmbedLoading(false);
    }
  }, [api, canManageEmbed, id, t]);

  const handleOpenEmbedDialog = useCallback(() => {
    setEmbedDialogOpen(true);
    loadEmbed();
  }, [loadEmbed]);

  const handleCloseEmbedDialog = useCallback(() => {
    if (embedActionLoading) return;
    setEmbedDialogOpen(false);
    setEmbedError(null);
  }, [embedActionLoading]);

  const publishEmbed = useCallback(async () => {
    if (!id) return;
    setEmbedActionLoading(true);
    setEmbedError(null);
    try {
      const data = await api.post(`/products/${id}/embed`, {});
      setEmbed(data);
    } catch (err: unknown) {
      setEmbedError(extractApiErrorMessage(err) || t('productDetails.embedPublishFailed'));
    } finally {
      setEmbedActionLoading(false);
    }
  }, [api, id, t]);

  const disableEmbed = useCallback(async () => {
    if (!id) return;
    setEmbedActionLoading(true);
    setEmbedError(null);
    try {
      const data = await api.del(`/products/${id}/embed`);
      setEmbed(data);
    } catch (err: unknown) {
      setEmbedError(extractApiErrorMessage(err) || t('productDetails.embedDisableFailed'));
    } finally {
      setEmbedActionLoading(false);
    }
  }, [api, id, t]);

  const rotateEmbed = useCallback(async () => {
    if (!id) return;
    setEmbedActionLoading(true);
    setEmbedError(null);
    try {
      const data = await api.post(`/products/${id}/embed/rotate`, {});
      setEmbed(data);
    } catch (err: unknown) {
      setEmbedError(extractApiErrorMessage(err) || t('productDetails.embedRotateFailed'));
    } finally {
      setEmbedActionLoading(false);
    }
  }, [api, id, t]);

  const handleCopyEmbedCode = useCallback((code: string) => {
    copyToClipboard(code);
  }, []);

  // Delete handlers
  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!product || !id) return;
    
    try {
      setDeleting(true);
      await api.del(`/products/${id}`);
      // Navigate back to products list after successful deletion
      navigate('/products');
    } catch (err) {
      setError('Failed to delete product');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [product, id, api, navigate]);

  const handleSdsUpload = useCallback(async (file: File, extractLabelTextFromClp: boolean) => {
    if (!id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('extract_label_text_from_clp', extractLabelTextFromClp ? 'true' : 'false');
    if (product?.base_language) {
      formData.append('source_language', product.base_language);
    }

    setSdsUploading(true);
    setSdsError(null);
    setSdsWarning(null);
    try {
      const uploaded = await api.postFormData('/files/sds', formData);
      const extractedGhsCodes = uploaded.conversion?.ghs_codes;
      await api.put(`/products/${id}/sds`, {
        sds_file_id: uploaded.id,
        sds_clp_extraction_id: uploaded.conversion?.id ?? undefined,
        base_language_clp_text_override: uploaded.conversion?.extracted_clp_text ?? undefined,
        ghs_pictograms: extractedGhsCodes && extractedGhsCodes.length > 0 ? extractedGhsCodes : undefined,
      });
      await fetchProductData();
      if (
        uploaded.conversion?.status === 'failed' ||
        uploaded.conversion?.status === 'markdown_conversion_failed'
      ) {
        setSdsWarning(uploaded.conversion.error_message || t('products.sdsConversionFailedWarning'));
      }
    } catch (err: unknown) {
      setSdsError(extractApiErrorMessage(err) || t('productDetails.sdsUploadFailed'));
    } finally {
      setSdsUploading(false);
    }
  }, [api, fetchProductData, id, product?.base_language, t]);

  const handleSdsDelete = useCallback(async () => {
    if (!id || !product?.sds) return;

    setSdsDeleting(true);
    setSdsError(null);
    try {
      const updatedProduct = await api.del(`/products/${id}/sds`);
      setProduct(updatedProduct);
    } catch (err: unknown) {
      setSdsError(extractApiErrorMessage(err) || t('productDetails.sdsDeleteFailed'));
    } finally {
      setSdsDeleting(false);
    }
  }, [api, id, product?.sds, t]);

  const handleSdsDownload = useCallback(async () => {
    if (!id || !product?.sds) return;

    setSdsDownloading(true);
    setSdsError(null);
    try {
      const response = await api.getResponse(`/products/${id}/sds/download`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = product.sds.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err: unknown) {
      setSdsError(extractApiErrorMessage(err) || t('productDetails.sdsDownloadFailed'));
    } finally {
      setSdsDownloading(false);
    }
  }, [api, id, product?.sds, t]);

  // Computed values
  const baseLanguageTranslation = translations.find(t => t.language === product?.base_language);
  const otherTranslations = translations.filter(t => t.language !== product?.base_language);

  return {
    // Data
    product,
    translations,
    baseLanguageTranslation,
    otherTranslations,
    error,
    sdsError,
    sdsWarning,
    loading,
    
    // Edit state
    isEditing,
    editForm,
    editFieldErrors,
    
    // Delete state
    deleteDialogOpen,
    deleting,
    
    // Version history state
    versionHistoryOpen,
    selectedLanguage,
    versionHistory,
    historyLoading,
    previewVersion,
    previewLoading,
    compareDiff,
    compareLoading,
    canRollbackTranslation,
    rollbackLoading,
    canManageEmbed,
    canAddSds,
    canReplaceSds,
    canDeleteSds,
    sdsUploading,
    sdsDeleting,
    sdsDownloading,
    embedDialogOpen,
    embed,
    embedLoading,
    embedActionLoading,
    embedError,

    // Handlers
    handleEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleFormChange,
    handlePictogramsChange,
    handleNavigateToTranslate,
    handleEditTranslation,
    handleCopyText,
    handleOpenEmbedDialog,
    handleCloseEmbedDialog,
    publishEmbed,
    disableEmbed,
    rotateEmbed,
    handleCopyEmbedCode,
    openVersionHistory,
    openVersionPreview,
    openVersionCompareWithCurrent,
    rollbackTranslationVersion,
    backToVersionHistoryList,
    closeVersionHistory,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSdsUpload,
    handleSdsDelete,
    handleSdsDownload,
  };
};
